import { captureEvent, posthogDistinctId } from "@/lib/analytics"

const LANDING_KEY = "archie_landing_path"
const PATHS_KEY = "archie_visited_paths"
const NUDGE_KEY = "archie_nudge_dismissed"
const MAX_PATHS = 25

export interface ArchieClientContext {
  locale?: string
  country_code?: string
  landing_path: string
  current_path: string
  visited_paths: string[]
  posthog_distinct_id?: string
  browser_language?: string
  timezone?: string
}

export function trackArchiePageview(pathname: string = window.location.pathname): void {
  try {
    if (!sessionStorage.getItem(LANDING_KEY)) {
      sessionStorage.setItem(LANDING_KEY, pathname)
      window.posthog?.register_once?.({ initial_landing_page: pathname })
    }
    const raw = sessionStorage.getItem(PATHS_KEY)
    const paths: string[] = raw ? (JSON.parse(raw) as string[]) : []
    if (paths[paths.length - 1] !== pathname) {
      paths.push(pathname)
      sessionStorage.setItem(PATHS_KEY, JSON.stringify(paths.slice(-MAX_PATHS)))
    }
  } catch {
    // sessionStorage may be unavailable
  }
}

export function buildArchieClientContext(): ArchieClientContext {
  trackArchiePageview()
  const browserLanguage =
    typeof navigator !== "undefined" ? navigator.language || navigator.languages?.[0] : undefined
  const timezone =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined

  let landing = window.location.pathname
  let visited: string[] = [window.location.pathname]
  try {
    landing = sessionStorage.getItem(LANDING_KEY) || landing
    const raw = sessionStorage.getItem(PATHS_KEY)
    if (raw) visited = JSON.parse(raw) as string[]
  } catch {
    // ignore
  }

  const posthog = window.posthog
  const country =
    (posthog?.get_property?.("$geoip_country_code") as string | undefined) ||
    (posthog?.get_property?.("$geoip_country_name") as string | undefined)

  return {
    country_code: typeof country === "string" && country.length === 2 ? country : undefined,
    landing_path: landing,
    current_path: window.location.pathname,
    visited_paths: visited,
    posthog_distinct_id: posthogDistinctId(),
    browser_language: browserLanguage,
    timezone,
    locale: browserLanguage?.split("-")[0],
  }
}

export function captureArchieEvent(event: string, props: Record<string, unknown> = {}): void {
  captureEvent(event, props)
}

export function nudgeDismissed(): boolean {
  try {
    return sessionStorage.getItem(NUDGE_KEY) === "1"
  } catch {
    return false
  }
}

export function dismissNudge(): void {
  try {
    sessionStorage.setItem(NUDGE_KEY, "1")
  } catch {
    // ignore
  }
}

export function shouldHideArchie(pathname: string = window.location.pathname): boolean {
  if (pathname.startsWith("/admin")) return true
  if (/^\/chats\/\d+/.test(pathname)) return true
  if (pathname.startsWith("/cable")) return true
  if (pathname === "/404") return true
  return false
}

export const ARCHIE_GREETINGS: Record<string, string> = {
  en: "Hi — I'm Archie. Need help with this page?",
  es: "Hola — soy Archie. ¿Necesitas ayuda con esta página?",
  pt: "Olá — eu sou o Archie. Precisa de ajuda com esta página?",
  fr: "Bonjour — je suis Archie. Besoin d'aide sur cette page ?",
  de: "Hallo — ich bin Archie. Brauchst du Hilfe auf dieser Seite?",
  it: "Ciao — sono Archie. Ti serve aiuto in questa pagina?",
  ja: "こんにちは、Archieです。このページでお手伝いしましょうか？",
  ko: "안녕하세요, Archie입니다. 이 페이지에서 도움이 필요하신가요?",
  zh: "你好，我是 Archie。需要我帮你看这个页面吗？",
  nl: "Hoi — ik ben Archie. Hulp nodig op deze pagina?",
}

export const ARCHIE_SIGNUP_PITCH_EXPERIMENT = "archie_signup_pitch"
export const ARCHIE_SIGNUP_PITCH_VARIANT = "signup_pitch"
export const ARCHIE_SIGNUP_PITCH_ASSIGNMENT_URL = "/api/field_tests/archie_signup_pitch"

/** Keep in sync with ArchieSignupPitchCopy in app/models/archie_signup_pitch_copy.rb. */
export const ARCHIE_SIGNUP_PITCHES: Record<string, string> = {
  en: "Hi — I'm Archie. Create a free Arc Research account to save reports, get COT alerts, and pick up where you left off. Reply with your email and I'll send a sign-up link.",
  es: "Hola — soy Archie. Crea una cuenta gratuita de Arc Research para guardar informes, recibir alertas COT y continuar donde lo dejaste. Responde con tu correo y te envío el enlace de registro.",
  pt: "Olá — eu sou o Archie. Crie uma conta gratuita da Arc Research para salvar relatórios, receber alertas COT e continuar de onde parou. Responda com seu e-mail e eu envio o link de cadastro.",
  fr: "Bonjour — je suis Archie. Créez un compte Arc Research gratuit pour enregistrer des rapports, recevoir des alertes COT et reprendre où vous vous étiez arrêté. Répondez avec votre e-mail et je vous envoie le lien d'inscription.",
  de: "Hallo — ich bin Archie. Erstelle ein kostenloses Arc Research-Konto, um Berichte zu speichern, COT-Alerts zu erhalten und dort weiterzumachen, wo du aufgehört hast. Antworte mit deiner E-Mail und ich sende dir den Anmeldelink.",
  it: "Ciao — sono Archie. Crea un account Arc Research gratuito per salvare i report, ricevere avvisi COT e riprendere da dove avevi lasciato. Rispondi con la tua email e ti invio il link di registrazione.",
  ja: "こんにちは、Archieです。無料の Arc Research アカウントを作ると、レポートの保存、COTアラート、続きからの再開ができます。メールアドレスを返信してください。登録リンクをお送りします。",
  ko: "안녕하세요, Archie입니다. 무료 Arc Research 계정을 만들면 리포트를 저장하고, COT 알림을 받고, 이어서 볼 수 있어요. 이메일 주소로 답장해 주시면 가입 링크를 보내 드릴게요.",
  zh: "你好，我是 Archie。免费注册 Arc Research 账号后可以保存报告、接收 COT 提醒，并从上次停下的地方继续。回复你的邮箱，我会发送注册链接。",
  nl: "Hoi — ik ben Archie. Maak een gratis Arc Research-account om rapporten te bewaren, COT-alerts te krijgen en verder te gaan waar je was gebleven. Antwoord met je e-mail en ik stuur een aanmeldlink.",
}

export function fieldTestAssignmentUrl(experimentUrl: string): string {
  const url = new URL(experimentUrl, window.location.origin)
  const pageParams = new URLSearchParams(window.location.search)
  pageParams.forEach((value, key) => {
    if (key.startsWith("field_test")) {
      url.searchParams.set(key, value)
    }
  })
  return url.toString()
}

export function fieldTestQueryOverride(): Record<string, string> | undefined {
  const pageParams = new URLSearchParams(window.location.search)
  const override: Record<string, string> = {}
  pageParams.forEach((value, key) => {
    const match = key.match(/^field_test\[(.+)\]$/)
    if (match?.[1]) override[match[1]] = value
  })
  return Object.keys(override).length > 0 ? override : undefined
}

export const ARCHIE_NUDGES: Record<string, string> = {
  en: "Need help?",
  es: "¿Necesitas ayuda?",
  pt: "Precisa de ajuda?",
  fr: "Besoin d'aide ?",
  de: "Brauchst du Hilfe?",
  it: "Ti serve aiuto?",
  ja: "お手伝いしましょうか？",
  ko: "도움이 필요하신가요?",
  zh: "需要帮助吗？",
  nl: "Hulp nodig?",
}

export function localizedCopy(map: Record<string, string>, locale: string | undefined): string {
  if (!locale) return map.en
  return map[locale] || map[locale.split("-")[0] || ""] || map.en
}
